import { TILE } from '../map/world';
import { toast } from '../dom';
import { startCoach, type CoachHandle, type CoachStep } from './coach';
import type { FarmCtx } from '../views/farm/context';
import { tutorialStep, setTutorialStep } from './state';
import type { SandboxTargets } from './sandbox-world';

/**
 * Phase 1 of the tutorial: a calm, always-day, mob-free controls sandbox on the
 * little 20×50 glade. Teaches move → chop → mine with the native pointer (ring
 * on the real tool, arrow on the real tree/rock). On completion it hands off to
 * the real world (see farm.ts), where the on-map tour continues.
 */
export function startSandboxTour(
  c: FarmCtx, host: HTMLElement, targets: SandboxTargets, onComplete: (skipped: boolean) => void,
): CoachHandle | null {
  try {
    const worldToScreen = (tx: number, ty: number) => {
      const cam = c.cam;
      if (!cam) return null;
      const sc = cam.getScale();
      return { x: (tx + 0.5) * TILE * sc + cam.world.x, y: (ty + 0.5) * TILE * sc + cam.world.y };
    };
    const dom = (sel: string) => () => document.querySelector(sel);
    const heroKey = () => (c.player ? `${Math.floor(c.player.x / TILE)},${Math.floor(c.player.y / TILE)}` : '');
    let moveFrom = '';

    /**
     * Point at the glade's tree/rock ONLY while it is still standing.
     *
     * These two steps aim at a FIXED tile, because the glade is hand-built and
     * there is exactly one of each. That is also how the arrow ended up parked on
     * a stump: the moment the tree comes down the tile still resolves, so the
     * bouncing arrow kept pointing at the leftover trunk until the step happened
     * to advance — and if anything delayed that advance, it sat there. Owner, more
     * than once: "arrow and axe on the tree leg after tree cut."
     *
     * Returning null hides the arrow outright (coach.ts treats a null target as
     * nothing to point at), so the instant the node is consumed the marker is
     * gone, whatever the step does next.
     */
    const whileStanding = (at: { tx: number; ty: number }, spent: () => boolean) => () => (spent() ? null : at);
    const anyFelled = () => (c.felledUntil?.size ?? 0) > 0 || (c.clearedStumps?.size ?? 0) > 0;
    const anyMined = () => (c.minedUntil?.size ?? 0) > 0;

    const steps: CoachStep[] = [
      { title: 'Welcome to Terraboma', manualOk: true,
        text: 'This quiet glade is your training ground — no danger here. Let\'s learn the controls.' },
      { title: 'Move', text: 'Walk with W A S D or the arrow keys — or click the ground to walk there.',
        onEnter: () => { moveFrom = heroKey(); },
        until: () => !!moveFrom && heroKey() !== moveFrom },
      { title: 'Pick the Axe', text: 'Click the Axe in your toolbar to hold it.',
        domAt: dom('[data-tut="tool-axe"]'), until: () => c.selectedTool === 'axe' },
      { title: 'Chop the tree', text: 'Click the tree to chop it for Wood.',
        // Sandbox props are client-only, so the server action may not fire —
        // advance the moment the tree is visually felled.
        arrowAt: whileStanding(targets.tree, anyFelled), onActionType: 'wood.chop',
        until: () => (c.felledUntil?.size ?? 0) > 0 || (c.clearedStumps?.size ?? 0) > 0 },
      { title: 'Pick the Pickaxe', text: 'Now click the Pickaxe.',
        domAt: dom('[data-tut="tool-pickaxe"]'), until: () => c.selectedTool === 'pickaxe' },
      { title: 'Mine the rock', text: 'Click the rock to mine Ore & Stone.',
        arrowAt: whileStanding(targets.rock, anyMined), onActionType: 'mine.break',
        until: () => (c.minedUntil?.size ?? 0) > 0 },
      { title: 'Ready!', manualOk: true,
        text: 'You\'ve got the basics. Step through the gate and enter the real world of Terraboma…' },
    ];

    return startCoach({
      host, steps, worldToScreen, chapter: 'Basics',
      // Resume silently after a reload. The glade's chop/mine tests are absolute,
      // so a step whose node is already down self-advances on the next poll.
      startAt: tutorialStep(), onStep: setTutorialStep,
      onDone: (skipped) => { toast('Entering Terraboma…'); onComplete(skipped); },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[tutorial] sandbox failed:', e);
    onComplete(true);
    return null;
  }
}
